import { useCallback, useEffect, useRef } from "react";

import { EventOrigin, Events, SectionMode, track } from "apis/procore";
import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { ToolbarButtonEntry, toolbarsActions, useDispatchToolbars } from "contexts/toolbars";
import { measureActions } from "features/measure";
import { Picker, renderActions, selectPicker } from "features/render";
import SectionPlaneIcon from "media/icons/section-plane.svg?react";

import { Toolbar } from "../Toolbar";
import { flyOutMenuHandler } from "./utils";

interface SectionFlyoutToolbarProps {
    onToolSelect?: () => void;
    id?: string;
    primaryToolbarId: string;
}

/**
 * Flyout toolbar with section tools (section plane, section box).
 */
export function SectionFlyoutToolbar({
    onToolSelect,
    id = "section-flyout-toolbar",
    primaryToolbarId,
}: SectionFlyoutToolbarProps) {
    const toolbarDispatch = useDispatchToolbars();
    const dispatch = useAppDispatch();

    const currentPicker = useAppSelector(selectPicker);
    const currentPickerRef = useRef(currentPicker);
    const onToolSelectRef = useRef(onToolSelect);

    currentPickerRef.current = currentPicker;
    onToolSelectRef.current = onToolSelect;

    const handlePickerClick = useCallback(
        (targetPicker: Picker) => {
            if (currentPicker === targetPicker) {
                dispatch(renderActions.stopPicker(targetPicker));
            } else {
                dispatch(renderActions.setPicker(targetPicker));
                void track(Events.clipPlane.enabled, { mode: SectionMode.ClipPlane, origin: EventOrigin.Toolbar });
            }
            onToolSelectRef.current?.();
        },
        [dispatch, currentPicker],
    );

    useEffect(() => {
        const buttons: ToolbarButtonEntry[] = [
            {
                config: {
                    id: "section-plane-picker",
                    icon: <SectionPlaneIcon />,
                    onClick: () => handlePickerClick(Picker.SectionPlane),
                    title: "Section Plane (S)",
                    tooltip: {
                        toolName: "sectionPlane",
                        shortcutKey: "",
                        alwaysVisible: true,
                        placement: "left",
                    },
                },
                state: {
                    active: currentPicker === Picker.SectionPlane,
                    disabled: false,
                    visible: true,
                },
            },
        ];

        toolbarDispatch(
            toolbarsActions.addToolbar({
                id,
                buttons,
            }),
        );

        dispatch(measureActions.setMenuOpen(false));

        const disposeMenuHandler = flyOutMenuHandler({
            flyoutMenuId: id,
            toolbarId: primaryToolbarId,
            close: () => {
                onToolSelectRef.current?.();
            },
        });

        return () => {
            disposeMenuHandler();
            toolbarDispatch(toolbarsActions.removeToolbar(id));
        };
    }, [dispatch, toolbarDispatch, id, primaryToolbarId, handlePickerClick, currentPicker]);

    useEffect(() => {
        toolbarDispatch(
            toolbarsActions.bulkUpdateButtonStates(id, {
                "section-plane-picker": { active: currentPicker === Picker.SectionPlane },
            }),
        );
    }, [toolbarDispatch, id, currentPicker]);

    return <Toolbar id={id} variant="fly-out" />;
}
